import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { catchError, forkJoin, map, of, tap } from 'rxjs';
import { GetCasePermissionsUseCase } from 'src/app/auth/application/use-cases/get-case-permissions/get-case-permissions.use-case';
import { HasCasePermissionUseCase } from 'src/app/auth/application/use-cases/has-case-permission/has-case-permission.use-case';
import { CasePermission } from 'src/app/auth/domain/enums/case-permission.enum';
import { GetMyManagedTeamsUseCase } from '@features/cases';
import { NavBarProvider, NavItemSource } from '../providers';

export const CASES_NAV_ITEMS: NavItemSource<CasePermission>[] = [
  {
    id: 'cases-dashboard',
    title: 'Dashboard',
    anchorHref: '/cases/dashboard',
    requiredPermission: CasePermission.CaseManagementViewOpen,
  },
  {
    id: 'cases-intake-queue',
    title: 'Intake Queue',
    anchorHref: '/cases/intake-queue',
    requiredPermission: CasePermission.CaseManagementViewIntakeQueue,
  },
  {
    id: 'cases-new-case',
    title: 'New Case',
    anchorHref: '/cases/create',
    requiredPermission: CasePermission.CaseManagementCreate,
  },
  {
    id: 'cases-approvals',
    title: 'Approvals',
    anchorHref: '/cases/approvals',
    requiredPermission: CasePermission.CaseManagementApprove,
  },
  {
    id: 'cases-team-workload',
    title: 'Team Workload',
    anchorHref: '/cases/workload',
  },
  {
    id: 'cases-my-watches',
    title: 'My Watches',
    anchorHref: '/cases/my-watches',
    requiredPermission: CasePermission.CaseManagementViewOpen,
  },
  {
    id: 'cases-audit-log',
    title: 'Audit Log',
    anchorHref: '/cases/audit-log',
    requiredPermission: CasePermission.CaseManagementViewAuditLog,
  },
  {
    id: 'cases-reports',
    title: 'Reports',
    anchorHref: '/cases/reports',
    requiredPermission: CasePermission.AnalyticsView,
  },
  {
    id: 'cases-admin',
    title: 'Admin Settings',
    anchorHref: '/cases/admin',
    requiredPermission: CasePermission.CaseManagementAccessSettings,
  },
];

export const setCasesNavigationResolver: ResolveFn<NavItemSource<CasePermission>[]> = () => {
  const navBarProvider = inject(NavBarProvider);
  const getPermissionsUseCase = inject(GetCasePermissionsUseCase);
  const hasPermissionUseCase = inject(HasCasePermissionUseCase);
  const getManagedTeamsUseCase = inject(GetMyManagedTeamsUseCase);

  navBarProvider.setLoadingNavigation(true);

  return forkJoin({
    _: getPermissionsUseCase.execute(),
    teams: getManagedTeamsUseCase.execute().pipe(catchError(() => of([]))),
  }).pipe(
    map(({ teams }) => {
      const hasTeams = teams.length > 0;
      return CASES_NAV_ITEMS.filter((source) => {
        if (source.id === 'cases-team-workload') return hasTeams;
        if (source.id === 'cases-new-case') {
          return hasPermissionUseCase.execute(CasePermission.CaseManagementCreate) &&
            !hasPermissionUseCase.execute(CasePermission.CaseManagementViewOpen);
        }
        if (!source.requiredPermission) return true;
        return hasPermissionUseCase.execute(source.requiredPermission);
      });
    }),
    tap((filtered) => {
      navBarProvider.updateNavigation(filtered);
      navBarProvider.setLoadingNavigation(false);
    }),
    catchError(() => {
      navBarProvider.updateNavigation([]);
      navBarProvider.setLoadingNavigation(false);
      return of([]);
    }),
  );
};
